import type React from "react";
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Link2, Upload } from "lucide-react";
import { theme, type as text, btnSecondary } from "../../theme";
import { api } from "../../api/client";
import { fmtBytes } from "../../utils/format";
import { fmtDate } from "../../lib/format";
import { DetailFields, DetailSection, EditButton, RecordActions, type DetailField } from "../RecordView";
import { ControlStatusBadge, CONTROL_STATUS_EXPERT, CONTROL_STATUS_NOVICE, displayRef } from "../controlStatus";
import { ControlComments } from "../ControlComments";
import { ControlTagsRow, type TagDimension } from "../ControlTags";
import { RelatedRecords } from "../RelatedRecords";
import { RecordChecks, type RecordCheckResult } from "../RecordChecks";
import { MappedFindingPointers } from "../FindingCrossChecks";
import { ControlAttestation } from "../ControlAttestation";

// The one detail layout a control opens into on every standard page — the same read-first
// shape as a register record (components/RecordView.tsx):
//
//   Requirement      what the standard asks, the page's guidance, and the evidence expected
//   (page sections)  what only this standard has: the legal threshold test, the CE+ note
//   Assessment       status, approval and the AI's note, read-only; Edit (controls:write) opens
//                    the status choices
//   Record checks    the platform's tests of the organisation's own registers, beside the AI
//                    verdict (a failed one caps it); its own section, never inside another panel
//   Evidence         the documents attached, with upload / link / remove (documents:write)
//   Also maps to     approved mappings to controls in the organisation's other standards
//                    (GET /control-mappings/control/:controlId); hidden when there are none
//   Notes           the control's note thread
//   Tags             department / office / jurisdiction / type
//   Related records  every record filed the same way
//   AI insights      the latest check's findings or on-demand recommendations
//   Actions          anything that resets or clears the record, under its own heading
//
// Nothing here scores: pages pass the status they hold and the call that sets it.

export interface EvidenceDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  controls: Array<{ frameworkId: string; controlRef: string }>;
}

/** What the Evidence section needs to list, attach and detach documents for one control. */
export interface EvidenceBinding {
  docs: EvidenceDoc[];
  /** The framework the document tag is filed under (cyber_essentials_plus for CE+). */
  frameworkId: string;
  /** The control reference the document tag carries (the control id, or the CE key). */
  controlRef: string;
  /** Called after any change so the page reloads its documents. */
  reload: () => unknown;
}

export interface StatusOption {
  value: string;
  label: string;
  /** Tooltip: "a consultant will review and approve" on Request compliant. */
  title?: string;
}

/** The status choices every SoA-backed standard offers; COMPLIANT is a request a consultant approves. */
export function soaStatusOptions(novice: boolean): StatusOption[] {
  return novice
    ? [
        { value: "NOT_STARTED", label: "Not started yet" },
        { value: "IN_PROGRESS", label: "Working on it" },
        { value: "GAP", label: "Need to fix this" },
        { value: "COMPLIANT", label: "Already in place", title: "Request that this is marked as already in place — a consultant will review and confirm" },
      ]
    : [
        { value: "NOT_STARTED", label: "Not assessed" },
        { value: "IN_PROGRESS", label: "In progress" },
        { value: "GAP", label: "Gap identified" },
        { value: "COMPLIANT", label: "Request compliant", title: "Request compliant status — a consultant will review and approve" },
      ];
}

export interface ControlDetailProps {
  /** The id tags, notes and related records are keyed by. */
  controlId: string;
  requirement?: React.ReactNode;
  /** The page's own guidance for this control (a key audit question, the CAF principle). */
  guidance?: React.ReactNode;
  checklist?: string[];
  checklistLabel?: string;
  /** Sections only this standard has, shown after the requirement. */
  sections?: React.ReactNode;

  status: string;
  novice?: boolean;
  /** Label of the status field ("Compliance status", "Operating effectiveness"). */
  statusLabel?: string;
  pending?: boolean;
  pendingText?: string;
  /** Why the consultant declined the last change (the row went back to its approved values). */
  rejectionReason?: string | null;
  aiNote?: string | null;
  /** Record checks on this control (GET /controls/soa row, or useRecordChecks for other pages). */
  recordChecks?: RecordCheckResult[];
  /** More read-only facts beside the status (applicability, last updated). */
  assessmentFields?: DetailField[];
  /** Controls that sit inside the Assessment section (the legal applicability override). */
  assessmentExtra?: React.ReactNode;
  statusOptions?: StatusOption[];
  onSetStatus?: (value: string) => unknown;
  /** controls:write — shows Edit on the assessment. */
  canEdit: boolean;
  saving?: boolean;

  evidence?: EvidenceBinding | null;
  /** documents:write — upload, link and remove evidence. */
  canAttach?: boolean;

  notes: {
    threadId: string;
    legacyNote?: string | null;
    onPost?: (body: string) => void;
    label?: string;
    placeholder?: string;
    consultantMode?: boolean;
  };
  tagDimensions?: TagDimension[];
  /** Show the Related records card (default true). */
  related?: boolean;
  insights?: React.ReactNode;
  actions?: React.ReactNode;
  actionsNote?: string;
}

export function ControlDetail(p: ControlDetailProps) {
  const [editing, setEditing] = useState(false);
  const table = p.novice ? CONTROL_STATUS_NOVICE : CONTROL_STATUS_EXPERT;
  const canChange = p.canEdit && !!p.onSetStatus && !!p.statusOptions?.length;

  const assessment: DetailField[] = [
    { label: p.statusLabel ?? (p.novice ? "How are we doing with this?" : "Compliance status"), value: <ControlStatusBadge status={p.status} novice={p.novice} /> },
    ...(p.pending ? [{ label: "Approval", value: p.pendingText ?? (p.novice ? "Your consultant is reviewing this" : "Awaiting consultant approval") }] : []),
    ...(!p.pending && p.rejectionReason ? [{ label: "Your consultant declined the last change", value: p.rejectionReason }] : []),
    ...(p.assessmentFields ?? []),
  ];

  return (
    <div>
      {(p.requirement || p.guidance || (p.checklist && p.checklist.length > 0)) && (
        <DetailSection title="Requirement">
          {p.requirement && <div style={{ ...text.body, marginBottom: p.guidance || p.checklist?.length ? 10 : 0 }}>{p.requirement}</div>}
          {p.guidance && <div style={{ ...text.hint, marginBottom: p.checklist?.length ? 10 : 0 }}>{p.guidance}</div>}
          {p.checklist && p.checklist.length > 0 && (
            <>
              <div style={text.label}>{p.checklistLabel ?? (p.novice ? "What you'll need to provide" : "Evidence expected")}</div>
              <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
                {p.checklist.map((c, i) => <li key={i} style={text.hint}>{c}</li>)}
              </ul>
            </>
          )}
        </DetailSection>
      )}

      {p.sections}

      <DetailSection
        title="Assessment"
        aside={canChange && !editing ? <EditButton onClick={() => setEditing(true)} /> : undefined}
      >
        <DetailFields fields={assessment} />
        {p.aiNote && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", marginTop: 10, background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, ...text.hint, color: theme.color.warningText }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{p.aiNote}</span>
          </div>
        )}
        {p.assessmentExtra && <div style={{ marginTop: 10 }}>{p.assessmentExtra}</div>}
        {canChange && editing && (
          <div style={{ marginTop: 12 }}>
            <div style={text.label}>Set the status</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {p.statusOptions!.map((opt) => {
                const active = p.status === opt.value;
                const cfg = table[opt.value] ?? table.NOT_STARTED;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    title={opt.title}
                    disabled={p.saving}
                    onClick={async () => { await p.onSetStatus!(opt.value); setEditing(false); }}
                    style={{
                      padding: "6px 12px", borderRadius: 3, fontSize: text.hint.fontSize, fontWeight: 600, cursor: "pointer",
                      border: `1px solid ${active ? cfg.border : theme.color.borderStrong}`,
                      background: active ? cfg.bg : "transparent",
                      color: active ? cfg.color : theme.color.muted,
                      opacity: p.saving ? 0.6 : 1,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
              <button type="button" onClick={() => setEditing(false)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize }}>Cancel</button>
            </div>
          </div>
        )}
      </DetailSection>

      <MappedFindingPointers controlId={p.controlId} novice={p.novice} />

      <ControlAttestation controlId={p.controlId} canEdit={p.canEdit} />

      {p.recordChecks && p.recordChecks.length > 0 && (
        <DetailSection title="Record checks">
          <RecordChecks results={p.recordChecks} />
        </DetailSection>
      )}

      {p.evidence && <EvidenceSection binding={p.evidence} canAttach={!!p.canAttach} />}

      <AlsoMapsTo controlId={p.controlId} novice={p.novice} />

      <DetailSection title="Notes">
        <ControlComments
          controlId={p.notes.threadId}
          open={true}
          label={p.notes.label ?? "Notes"}
          placeholder={p.notes.placeholder}
          consultantMode={p.notes.consultantMode}
          legacyNote={p.notes.legacyNote}
          onPost={p.notes.onPost}
        />
      </DetailSection>

      <DetailSection title="Tags">
        <ControlTagsRow controlId={p.controlId} dimensions={p.tagDimensions} style={{ margin: 0 }} />
      </DetailSection>

      {p.related !== false && (
        <DetailSection title="Related records">
          <RelatedRecords entity={{ kind: "control", id: p.controlId }} defaultOpen={false} />
        </DetailSection>
      )}

      {p.insights && <DetailSection title="AI insights">{p.insights}</DetailSection>}

      {p.actions && p.canEdit && <RecordActions note={p.actionsNote}>{p.actions}</RecordActions>}
    </div>
  );
}

// ── Also maps to ─────────────────────────────────────────────────────────────

interface ControlMappingView {
  controlId: string;
  frameworkId: string;
  frameworkLabel: string;
  name: string;
  strength: "equivalent" | "partial" | "related";
  strengthLabel: string;
  source: "harmonised_structure" | "published" | "ai_draft";
  sourceRef: string | null;
  rationale: string | null;
  /** The organisation's SOA status on the mapped control; null where it holds none. */
  status: string | null;
}

/**
 * Controls in the organisation's other standards that an approved mapping joins to this one.
 * Informational: evidence accepted there may support this control, but each control is judged
 * on its own requirement. Renders nothing while loading, on failure, or when there are none.
 */
function AlsoMapsTo({ controlId, novice }: { controlId: string; novice?: boolean }) {
  const [mappings, setMappings] = useState<ControlMappingView[]>([]);

  useEffect(() => {
    let live = true;
    setMappings([]);
    api.get<{ mappings: ControlMappingView[] }>(`/control-mappings/control/${encodeURIComponent(controlId)}`)
      .then((d) => { if (live) setMappings(d.mappings ?? []); })
      .catch(() => { /* reference data only; the section stays hidden */ });
    return () => { live = false; };
  }, [controlId]);

  if (mappings.length === 0) return null;

  return (
    <DetailSection title={`Also maps to (${mappings.length})`}>
      <div style={{ ...text.hint, marginBottom: 10 }}>
        Evidence already accepted for these controls may support this one too. Each control is still judged on its own requirement.
      </div>
      <div>
        {mappings.map((m) => (
          <div key={`${m.frameworkId}:${m.controlId}`} style={{ padding: "8px 0", borderBottom: `1px solid ${theme.color.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={text.meta}>{m.frameworkLabel}</div>
                <div style={{ ...text.body, color: theme.color.heading, overflowWrap: "anywhere" }}>
                  <span style={{ ...text.ref, color: theme.color.ref, marginRight: 6 }}>{displayRef(m.controlId)}</span>
                  {m.name}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 2, border: `1px solid ${theme.color.borderStrong}`, color: theme.color.muted, whiteSpace: "nowrap" }}>
                  {m.strengthLabel}
                </span>
                {m.status && <ControlStatusBadge status={m.status} novice={novice} />}
              </div>
            </div>
            {m.rationale && <div style={{ ...text.meta, marginTop: 4 }}>{m.rationale}</div>}
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

// ── Evidence ─────────────────────────────────────────────────────────────────

function EvidenceSection({ binding, canAttach }: { binding: EvidenceBinding; canAttach: boolean }) {
  const { docs, frameworkId, controlRef, reload } = binding;
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const failed = (e: unknown, fallback: string) => setEvidenceError(e instanceof Error && e.message ? `${fallback} ${e.message}` : fallback);
  const isLinked = (d: EvidenceDoc) => d.controls.some((t) => t.frameworkId === frameworkId && t.controlRef === controlRef);
  const linked = docs.filter(isLinked);
  const unlinked = docs.filter((d) => !isLinked(d));

  async function upload(file: File) {
    setUploading(true);
    setEvidenceError("");
    try {
      const { blobPath, uploadUrl } = await api.post<{ blobPath: string; uploadUrl: string }>(
        "/documents/request-upload",
        { fileName: file.name, contentType: file.type || "application/octet-stream" },
      );
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      // A refused storage upload records nothing: say so rather than returning to the button.
      if (!put.ok) throw new Error(`Storage refused the file (${put.status}).`);
      await api.post("/documents", { blobPath, fileName: file.name, sizeBytes: file.size, tags: [{ frameworkId, controlRef }] });
      await reload();
    } catch (e) {
      failed(e instanceof TypeError ? null : e, `${file.name} was not uploaded.`);
    } finally { setUploading(false); }
  }

  async function link(docId: string) {
    const doc = docs.find((d) => d.id === docId);
    if (!doc || isLinked(doc)) return;
    setEvidenceError("");
    const tags = [...doc.controls.map(({ frameworkId: f, controlRef: c }) => ({ frameworkId: f, controlRef: c })), { frameworkId, controlRef }];
    try {
      await api.patch(`/documents/${docId}/controls`, { tags });
      await reload();
      setLinking(false);
    } catch (e) { failed(e, "The document was not linked."); }
  }

  async function unlink(docId: string) {
    const doc = docs.find((d) => d.id === docId);
    if (!doc) return;
    setEvidenceError("");
    const tags = doc.controls
      .filter((t) => !(t.frameworkId === frameworkId && t.controlRef === controlRef))
      .map(({ frameworkId: f, controlRef: c }) => ({ frameworkId: f, controlRef: c }));
    try {
      await api.patch(`/documents/${docId}/controls`, { tags });
      await reload();
    } catch (e) { failed(e, "The document was not removed."); }
  }

  const small: React.CSSProperties = { ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize, display: "inline-flex", alignItems: "center", gap: 5 };

  return (
    <DetailSection title={`Evidence${linked.length ? ` (${linked.length})` : ""}`}>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }}
      />
      {linked.length === 0 ? (
        <div style={{ ...text.hint, marginBottom: canAttach ? 10 : 0 }}>No evidence attached yet.</div>
      ) : (
        <div style={{ marginBottom: canAttach ? 10 : 0 }}>
          {linked.map((doc) => (
            <div key={doc.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${theme.color.border}` }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ ...text.body, color: theme.color.heading, overflowWrap: "anywhere" }}>{doc.fileName}</span>
                <span style={{ ...text.meta, marginLeft: 8 }}>{fmtBytes(doc.sizeBytes)} · {fmtDate(doc.uploadedAt)}</span>
              </div>
              {canAttach && <button type="button" onClick={() => unlink(doc.id)} style={small}>Remove</button>}
            </div>
          ))}
        </div>
      )}
      {evidenceError && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 8 }}>{evidenceError}</div>}
      {canAttach && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ ...small, opacity: uploading ? 0.6 : 1 }}>
              <Upload size={12} />{uploading ? "Uploading…" : "Upload evidence"}
            </button>
            {unlinked.length > 0 && (
              <button type="button" onClick={() => setLinking((v) => !v)} style={small}>
                <Link2 size={12} />{linking ? "Cancel" : "Link existing document"}
              </button>
            )}
          </div>
          {linking && (
            <div style={{ marginTop: 10, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 6, maxHeight: 200, overflowY: "auto" }}>
              {unlinked.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => link(doc.id)}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 3, cursor: "pointer", background: "transparent", border: "none", textAlign: "left" }}
                >
                  <span style={{ ...text.hint, color: theme.color.heading }}>{doc.fileName}</span>
                  <span style={text.meta}>{fmtBytes(doc.sizeBytes)}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </DetailSection>
  );
}

/** How many documents are attached to a control, for the row's evidence count. */
export function evidenceCountFor(docs: EvidenceDoc[], frameworkId: string, controlRef: string): number {
  return docs.filter((d) => d.controls.some((t) => t.frameworkId === frameworkId && t.controlRef === controlRef)).length;
}
